/**
 * CAOS-594 — the platform size scale, as the step names a component offers.
 *
 * CAOS-586 adopted ONE scale of five steps, used for exactly three things: the box an icon draws in,
 * the diameter of an avatar, and the height of a control. The lengths live in `tokens.css` as
 * `--caos-size-<step>-height`, where a theme can override them. This module holds only the names, so
 * an input's options and the rules a stylesheet writes for them come from the same list and cannot
 * drift apart.
 *
 * It is deliberately not a size variant for every component. A piece takes a size from here only
 * when it is one of the three things the scale exists for.
 */
export const SIZE_STEPS = ['xs', 'sm', 'md', 'lg', 'xl'] as const;

export type SizeStep = (typeof SIZE_STEPS)[number];

/** The length a step stands for, as the token that carries it: `md` → `var(--caos-size-md-height)`. */
export function sizeLength(step: SizeStep): string {
  return `var(--caos-size-${step}-height)`;
}

/**
 * The rules for a `size` attribute: one for the default, which applies when the attribute is absent
 * or names no step, then one per step. `rules` receives the host selector and the step's length and
 * returns the CSS for them, so a piece can size more than its host from the same length.
 */
export function sizeRules(defaultStep: SizeStep, rules: (host: string, length: string) => string): string {
  return [
    rules(':host', sizeLength(defaultStep)),
    ...SIZE_STEPS.map((step) => rules(`:host([size="${step}"])`, sizeLength(step))),
  ].join('\n  ');
}

/**
 * CAOS-599 — the three steps a CONTROL takes, and the lengths that describe its inside.
 *
 * A control is one of the three things the scale exists for, but only three of the five steps are a
 * control: nothing is a 20px button and nothing is a 56px one. That is not a second scale — the
 * heights are the same tokens `sizeLength` returns — it is the part of the one scale a control can
 * stand on, said once so two controls cannot disagree about it.
 *
 * The inside of a control needs two more lengths that a square icon box does not: how much room the
 * label has around it, and how big the label is set. Those exist as tokens for these three steps
 * only, for the same reason.
 */
export const CONTROL_STEPS = ['sm', 'md', 'lg'] as const;

export type ControlStep = (typeof CONTROL_STEPS)[number];

/** The three lengths a control takes from a step of the scale. */
export interface ControlLengths {
  /** The control's height, as the token that carries it. Shared with every other sized piece. */
  readonly height: string;
  /** The room around the label. */
  readonly padding: string;
  /** The size the label is set at. */
  readonly font: string;
}

/** The lengths a control step stands for, as the tokens that carry them. */
export function controlLengths(step: ControlStep): ControlLengths {
  return {
    height: sizeLength(step),
    padding: `var(--caos-size-${step}-padding)`,
    font: `var(--caos-size-${step}-font)`,
  };
}

/**
 * The rules for a control's `size` attribute, the same shape as `sizeRules`: the default first, so
 * an absent or unrecognised `size` draws as the default rather than as nothing, then one per step.
 */
export function controlRules(
  defaultStep: ControlStep,
  rules: (host: string, lengths: ControlLengths) => string,
): string {
  return [
    rules(':host', controlLengths(defaultStep)),
    ...CONTROL_STEPS.map((step) => rules(`:host([size="${step}"])`, controlLengths(step))),
  ].join('\n  ');
}
